import { useForm } from 'react-hook-form'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import { useApp } from '../../hooks/useApp'
import { useUI } from '../../hooks/useUI'
import { adjustColor } from '../../theme/ajustColor'
import { useEffect, useState } from 'react'
import {
  setTiloPayPaymentData,
  useFormData,
  useTiloPayPaymentData
} from '../../queries/userGlobalQueries'
import { ProcessPaymentType } from '../../utils/data'
import { use } from 'i18next'
import { queryClient } from '../../queries/config'
import Loading from '../../components/Loading'
import { useCompanySpaces } from '../../queries/useCompanyQueries'
import FancyInput from '../../components/FancyInput'
import { CustomSelect } from '../../components/CustomSelect'

// Extender la interfaz Window para incluir REACT_APP_TILOPAY
declare global {
  interface Window {
    REACT_APP_TILOPAY: string
  }
}

interface TokenType {
  access_token: string
  token_type: string
  expires_in: number
}

interface TilopayCredentials {
  apiuser: string
  password: string
}

// Definir mensajes de error para usar en las funciones
const ERROR_MESSAGES = {
  errorObtainingTilopayToken: 'Error al obtener token de Tilopay',
  errorProcessingTilopayPayment: 'Error al procesar pago con Tilopay',
  errorProcessingPayment: 'Error al procesar el pago'
}

const loginTilopay = async (credentials: TilopayCredentials): Promise<TokenType> => {
  try {
    const data = {
      apiuser: credentials.apiuser,
      password: credentials.password
    }

    const response = await fetch(`${window.REACT_APP_TILOPAY}/login`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      throw new Error(ERROR_MESSAGES.errorObtainingTilopayToken)
    }

    return await response.json()
  } catch (error: any) {
    console.error('Error en loginTilopay:', error)
    throw error
  }
}

export const processPaymentTilopay = async (data: ProcessPaymentType, token: string) => {
  try {
    const response = await fetch(`${window.REACT_APP_TILOPAY}/processPayment`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      throw new Error(ERROR_MESSAGES.errorProcessingTilopayPayment)
    }

    return await response.json()
  } catch (error: any) {
    console.error('Error en processPaymentTilopay:', error)
    throw error
  }
}

interface TilopayPaymentProps {
  cartData: any
  currency: string
  onPaymentComplete?: () => void
  apiKey?: string
  apiuser?: string
  password?: string
}

const TilopayPayment = ({
  cartData,
  currency,
  onPaymentComplete,
  apiKey = '6038-7043-7246-8325-7793',
  apiuser = 'pE1fDw',
  password = 'FGZt74'
}: TilopayPaymentProps) => {
  const { t } = useTranslation()
  const { setErrorMsg } = useApp()
  const { darkMode } = useUI()
  const { data: companyData } = useCompanySpaces()

  // Generar paleta de colores basada en el modo
  const primaryColor = companyData?.body?.content[0]?.color || '#0A3A7A'
  const colors = adjustColor(primaryColor, darkMode ? 'dark' : 'light')

  const [orderNumber] = useState<string>(`ORD-${Date.now()}`)
  const { data: formData } = useFormData()
  const { data: tiloPayPaymentData } = useTiloPayPaymentData()
  const [loading, setLoading] = useState<boolean>(false)
  const [paymentIsready, setPaymentIsReady] = useState<boolean>(false)

  const validation = Yup.object().shape({
    redirect: Yup.string().required(t('fieldRequired')),
    key: Yup.string().required(t('fieldRequired')),
    amount: Yup.string().required(t('fieldRequired')),
    currency: Yup.string().required(t('fieldRequired')),
    billToFirstName: Yup.string()
      .required(t('fieldRequired'))
      .matches(/^[A-Za-z\s]+$/, t('onlyLetters')),
    billToLastName: Yup.string()
      .required(t('fieldRequired'))
      .matches(/^[A-Za-z\s]+$/, t('onlyLetters')),
    billToAddress: Yup.string().required(t('fieldRequired')),
    billToAddress2: Yup.string().required(t('fieldRequired')),
    billToCity: Yup.string().required(t('fieldRequired')),
    billToState: Yup.string().required(t('fieldRequired')),
    billToZipPostCode: Yup.string().required(t('fieldRequired')),
    billToCountry: Yup.string().required(t('fieldRequired')),
    billToTelephone: Yup.string()
      .required(t('fieldRequired'))
      .matches(/^[0-9]+$/, t('onlyNumbers'))
      .min(8, t('min8Chars'))
      .max(15, t('max15Chars')),
    billToEmail: Yup.string().required(t('fieldRequired')).email(t('invalidEmail')),
    orderNumber: Yup.string().required(t('fieldRequired'))
  })

  const {
    setValue,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<ProcessPaymentType>({
    resolver: yupResolver(validation) as any,
    mode: 'onChange'
  })

  const watchAllFields = watch()

  // Actualizar los valores cuando formData cambie
  useEffect(() => {
    const defaultValues = {
      redirect: `${window.location.origin}/approvePayment`,
      key: apiKey,
      amount: cartData?.allAmount?.toString() || '0',
      currency: currency || 'USD',
      billToFirstName: formData?.userName || '',
      billToLastName: formData?.userLastName || '',
      billToTelephone: formData?.userPhone || '',
      billToEmail: formData?.userEmail || '',
      orderNumber: orderNumber
    }

    // Actualizar tanto el estado global como el formulario local
    queryClient.setQueryData([setTiloPayPaymentData], (old: any) => ({
      ...old,
      ...defaultValues
    }))

    // Actualizar los valores del formulario
    Object.entries(defaultValues).forEach(([key, value]) => {
      setValue(key as keyof ProcessPaymentType, value)
    })
  }, [formData, apiKey, cartData, currency, orderNumber, setValue])

  // Sincronizar el formulario con tiloPayPaymentData
  useEffect(() => {
    if (tiloPayPaymentData) {
      Object.entries(tiloPayPaymentData).forEach(([key, value]) => {
        setValue(key as keyof ProcessPaymentType, value)
      })
    }
  }, [tiloPayPaymentData, setValue])

  const handlePaymentTilopay = async (data: ProcessPaymentType) => {
    try {
      setLoading(true)
      const getToken = await loginTilopay({
        apiuser,
        password
      })

      if (getToken) {
        const payment = await processPaymentTilopay(data, getToken.access_token)

        if (payment && payment.url) {
          queryClient.setQueryData([setTiloPayPaymentData], (old: any) => ({
            ...old,
            url: payment.url
          }))
          onPaymentComplete?.()
          setPaymentIsReady(true)
        }
      }
    } catch (error: any) {
      console.error(error)
      setErrorMsg && setErrorMsg(error.message || t('errorProcessingPayment'))
    } finally {
      setLoading(false)
    }
  }

  const updateField = (field: keyof ProcessPaymentType, value: string) => {
    queryClient.setQueryData([setTiloPayPaymentData], (old: any) => ({
      ...old,
      [field]: value
    }))
  }

  return (
    <div className="mt-8 rounded-lg shadow-md p-6">
      <h2 style={{ color: colors.text }} className="text-2xl font-semibold mb-6">
        {t('paymentInformation')}
      </h2>

      <form onSubmit={handleSubmit(handlePaymentTilopay)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FancyInput
            label={t('orderNumber')}
            value={watchAllFields.orderNumber}
            onChange={(e) => updateField('orderNumber', e.target.value)}
            required
            error={!!errors.orderNumber}
            helperText={errors.orderNumber?.message}
            readOnly
          />

          <FancyInput
            label={t('amount')}
            value={watchAllFields.amount}
            onChange={(e) => updateField('amount', e.target.value)}
            required
            error={!!errors.amount}
            helperText={errors.amount?.message}
            readOnly
          />

          <CustomSelect
            label={t('currency')}
            value={watchAllFields.currency}
            onChange={(e) => updateField('currency', e.target.value)}
            listItems={[currency]}
            required
            error={!!errors.currency}
            helperText={errors.currency?.message}
          />
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h3 style={{ color: colors.text }} className="text-lg font-medium mb-4">
            {t('personalInformation')}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FancyInput
              label={t('firstName')}
              value={watchAllFields.billToFirstName}
              onChange={(e) => updateField('billToFirstName', e.target.value)}
              required
              error={!!errors.billToFirstName}
              helperText={errors.billToFirstName?.message}
            />

            <FancyInput
              label={t('lastName')}
              value={watchAllFields.billToLastName}
              onChange={(e) => updateField('billToLastName', e.target.value)}
              required
              error={!!errors.billToLastName}
              helperText={errors.billToLastName?.message}
            />

            <FancyInput
              label={t('email')}
              type="email"
              value={watchAllFields.billToEmail}
              onChange={(e) => updateField('billToEmail', e.target.value)}
              required
              error={!!errors.billToEmail}
              helperText={errors.billToEmail?.message}
            />

            <FancyInput
              label={t('phone')}
              type="tel"
              value={watchAllFields.billToTelephone}
              onChange={(e) => updateField('billToTelephone', e.target.value)}
              required
              error={!!errors.billToTelephone}
              helperText={errors.billToTelephone?.message}
            />
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h3 style={{ color: colors.text }} className="text-lg font-medium mb-4">
            {t('billingAddress')}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FancyInput
              label={t('address')}
              value={watchAllFields.billToAddress}
              onChange={(e) => updateField('billToAddress', e.target.value)}
              required
              error={!!errors.billToAddress}
              helperText={errors.billToAddress?.message}
            />

            <FancyInput
              label={t('addressLine2')}
              value={watchAllFields.billToAddress2}
              onChange={(e) => updateField('billToAddress2', e.target.value)}
              required
              error={!!errors.billToAddress2}
              helperText={errors.billToAddress2?.message}
            />

            <FancyInput
              label={t('city')}
              value={watchAllFields.billToCity}
              onChange={(e) => updateField('billToCity', e.target.value)}
              required
              error={!!errors.billToCity}
              helperText={errors.billToCity?.message}
            />

            <FancyInput
              label={t('zipCode')}
              value={watchAllFields.billToZipPostCode}
              onChange={(e) => updateField('billToZipPostCode', e.target.value)}
              required
              error={!!errors.billToZipPostCode}
              helperText={errors.billToZipPostCode?.message}
            />
          </div>
        </div>

        {!paymentIsready && (
          <div className="flex justify-end pt-6">
            <button
              disabled={loading || paymentIsready}
              type="submit"
              style={{
                backgroundColor: loading || paymentIsready ? colors.light : colors.main,
                color: colors.contrast
              }}
              className="px-6 py-3 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 hover:opacity-90 disabled:cursor-not-allowed"
            >
              {loading || paymentIsready ? (
                <div className="flex items-center space-x-2">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>{t('validating')}</span>
                </div>
              ) : (
                t('generatePayment')
              )}
            </button>
          </div>
        )}
      </form>

      <div className="mt-8 flex items-center justify-center space-x-4">
        <div className="flex space-x-2">
          <div className="bg-white p-2 rounded">
            <img
              src="https://www.tilopay.com/images/LogoTilopay.svg"
              alt="Tilopay"
              className="h-8"
            />
          </div>
          <div className="bg-white p-1 rounded">
            <img
              src="https://www.mastercard.com/content/dam/public/mastercardcom/na/global-site/home/mc-logo-52.svg"
              alt="Mastercard"
              className="h-8"
            />
          </div>
          <div className="bg-white p-1 rounded">
            <img
              src="https://cdn.visa.com/v2/assets/images/logos/visa/blue/logo.png"
              alt="Visa"
              className="h-8"
            />
          </div>
          <div className="bg-white p-1 rounded">
            <img
              src="https://www.aexp-static.com/cdaas/one/statics/axp-static-assets/1.8.0/package/dist/img/logos/dls-logo-bluebox-solid.svg"
              alt="American Express"
              className="h-8"
            />
          </div>
        </div>
      </div>

      <p style={{ color: colors.text }} className="text-xs text-center mt-4 opacity-75">
        {t('securePayment')}
      </p>
    </div>
  )
}

export default TilopayPayment
